/**
 * Browser-side `zoom_view`: view-only, auto-run like a read (it never
 * touches contour data). Two kinds of request:
 *  - `focus-structure` ("show me the left lung"): resolve the name with the
 *    shared resolver, show the structure if hidden, select it if not
 *    selected, then centre and zoom all views on it;
 *  - `zoom-in` / `zoom-out`: repeat the viewer's own zoom step, stopping at
 *    the viewer's zoom limits and reporting how many steps took effect.
 * Every engine call is injected (React-free, unit-testable).
 */
import { resolveRoi, isPointRoiType } from '../core/roiResolver';
import { asRecord, toListed, type StructureFacts } from '../core';
import type { ZoomViewOutput } from '../../contract';

/** Same bound as the server contract (MAX_ZOOM_STEPS). */
export const MAX_ZOOM_STEPS = 5;

export interface ZoomViewDeps {
    /** False when no image/views are loaded. */
    readonly viewReady: boolean;
    readonly structures: ReadonlyArray<StructureFacts>;
    isVisible(roiNumber: number): boolean;
    isSelected(roiNumber: number): boolean;
    show(roiNumber: number): void;
    select(roiNumber: number): void;
    /** Centre and zoom all views on the structure (existing focus call). */
    focus(roiNumber: number): void;
    /** One zoom step; false when the viewer's zoom limit stopped it. */
    zoomStep(direction: 'in' | 'out'): boolean;
}

const toRef = (s: { roiNumber: number; name: string }) => ({
    roiNumber: s.roiNumber,
    name: s.name,
});

function unavailable(reason: string): ZoomViewOutput {
    return { status: 'unavailable', reason };
}

export function zoomView(input: unknown, deps: ZoomViewDeps): ZoomViewOutput {
    const o = asRecord(input);
    const action = o?.action;
    if (
        action !== 'focus-structure' &&
        action !== 'zoom-in' &&
        action !== 'zoom-out'
    ) {
        return unavailable('invalid zoom_view input');
    }
    if (!deps.viewReady) {
        return unavailable('no image is shown in the viewer');
    }

    if (action === 'zoom-in' || action === 'zoom-out') {
        const raw = o?.steps;
        const steps = raw === undefined ? 1 : raw;
        if (
            typeof steps !== 'number' ||
            !Number.isInteger(steps) ||
            steps < 1 ||
            steps > MAX_ZOOM_STEPS
        ) {
            return unavailable(
                `steps must be an integer from 1 to ${MAX_ZOOM_STEPS}`
            );
        }
        const direction = action === 'zoom-in' ? 'in' : 'out';
        let stepsApplied = 0;
        while (stepsApplied < steps && deps.zoomStep(direction)) {
            stepsApplied += 1;
        }
        return { status: 'ok', action, stepsApplied };
    }

    const roi = o?.roi;
    if (typeof roi !== 'string' || roi.length === 0) {
        return unavailable('focus-structure needs the structure name');
    }
    const resolution = resolveRoi(roi, deps.structures.map(toListed));
    if (resolution.kind === 'ambiguous') {
        return {
            status: 'ambiguous',
            reason: resolution.reason,
            candidates: resolution.candidates.map(toRef),
        };
    }
    if (resolution.kind === 'not-found') {
        return {
            status: 'not-found',
            suggestions: resolution.suggestions.map(toRef),
        };
    }
    const target = deps.structures.find(
        (s) => s.roiNumber === resolution.structure.roiNumber
    );
    if (target === undefined) {
        return unavailable('resolved structure not found');
    }
    // The existing focus call frames a structure's contour extent; a point
    // ROI or an empty structure has none to frame.
    if (isPointRoiType(target.type)) {
        return unavailable(`'${target.name}' is a point ROI`);
    }
    if (target.isEmpty) {
        return unavailable(`'${target.name}' has no contours to show`);
    }

    const madeVisible = !deps.isVisible(target.roiNumber);
    if (madeVisible) {
        deps.show(target.roiNumber);
    }
    if (!deps.isSelected(target.roiNumber)) {
        deps.select(target.roiNumber);
    }
    deps.focus(target.roiNumber);
    return {
        status: 'ok',
        action,
        structure: toRef(target),
        madeVisible,
    };
}
